import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  createTheme,
  MantineColorsTuple,
  MantineProvider,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import "./i18n";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import { BrowserRouter as Router } from "react-router-dom";
import { SessionProvider } from "./auth/SessionContext";
import { ConsentProvider } from "./components/CookieBanner/ConsentContext";
import CheckRefLink from "./CheckReflink";
import { ReferralProvider } from "./scripts/ReferralContext";
import CookieModal from "./components/CookieBanner/CookieModal";
import { LmuTvRoot } from "./lmutv/LmuTvRoot";

export const DEBUG = process.env.NODE_ENV === 'development';
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Top-level app switch: the same build serves the SimRacing hub AND the migrated
// LMUTV storefront. LMUTV takes over when served from its own domain (lmutv.de/.com)
// or reached via the /lmutv path on the hub. Either/or at the root — never a nested
// <Router> — with a basename so LMUTV's absolute routes resolve under both entries.
// A per-app build (e.g. a subdomain that only serves LMUTV) can force the shell
// with REACT_APP_APP=lmutv — then it renders LMUTV at the domain root regardless of
// hostname. Otherwise fall back to host/path detection: lmutv.de → LMUTV at root,
// /lmutv on the hub → LMUTV under that subpath.
const forcedApp = process.env.REACT_APP_APP;
const lmuTvHost = /(^|\.)lmutv\.(de|com)$/i.test(window.location.hostname);
const lmuTvPath =
  window.location.pathname === "/lmutv" || window.location.pathname.startsWith("/lmutv/");
const showLmuTv = forcedApp === "lmutv" || lmuTvHost || lmuTvPath;
const lmuTvBasename = forcedApp === "lmutv" || lmuTvHost ? "/" : "/lmutv";

const accDriveColors: MantineColorsTuple = [
  "#effcea",
  "#e3f2da",
  "#c6e5b6",
  "#a8d68f",
  "#8dc96d",
  "#7dc257",
  "#74be4c",
  "#62a73c",
  "#559434",
  "#468027",
];

const theme = createTheme({
  colors: {
    accDriveColors,
    dark: [
            '#C1C2C5',
            '#A6A7AB',
            '#909296',
            '#5c5f66',
            '#373A40',
            '#2C2E33',
            '#25262b',
            '#1A1B1E',
            '#141517',
            '#101113'
          ]
  },
});
root.render(
  <React.StrictMode>
    {showLmuTv ? (
      <LmuTvRoot basename={lmuTvBasename} />
    ) : (
      <MantineProvider defaultColorScheme="dark" theme={theme}>
        <ConsentProvider>
          <CheckRefLink />
          <CookieModal />
          <ModalsProvider>
            <Suspense fallback={<></>}>
              <Notifications />
              <Router>
                <ReferralProvider>
                  <SessionProvider>
                    <App />
                  </SessionProvider>
                </ReferralProvider>
              </Router>
            </Suspense>
          </ModalsProvider>
        </ConsentProvider>
      </MantineProvider>
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
